import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, { HTMLAttributes, useState } from 'react'
import { ExtranetTableExpansion } from './ExtranetTableExpansion'
import { joinClasses } from './joinClasses'

type ExtranetTableRowRootProps = {
  innerIsExpanded?: boolean
  noMargin?: boolean
  noBorders?: boolean
}

const ExtranetTableRowRoot = styled.div<ExtranetTableRowRootProps>`
  width: 100%;
  height: 100%;

  border-radius: ${({ noBorders }) => (noBorders ? 0 : 'var(--border-radius-small)')};
  margin-bottom: ${({ noMargin }) => (noMargin ? 0 : 'var(--spacing-small)')};
  box-sizing: border-box;
  overflow: hidden;

  &:nth-of-type(odd) {
    background-color: var(--table-even-row-background);

    .entable__expansion-row > .entable__row {
      background-color: var(--table-even-row-background);
    }

    ${(p) =>
      !p.noBorders &&
      css`
        border: ${p.innerIsExpanded
          ? 'var(--table-expand-row-border)'
          : '2px solid var(--color-accent-4)'};
      `}
  }
  &:nth-of-type(even) {
    background-color: var(--table-odd-row-background);

    .entable__expansion-row > .entable__row {
      background-color: var(--table-odd-row-background);
    }

    ${(p) =>
      !p.noBorders &&
      css`
        border: ${p.innerIsExpanded
          ? 'var(--table-expand-row-border)'
          : '2px solid var(--color-accent-4)'};
      `}
  }
`

const ExtranetTableRowWrapper = styled.div<{ isClickable?: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  margin: 0;
  padding: 0;

  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
`

type ExtranetTableRowProps = {
  isExpanded?: boolean
  expand?: true | React.ReactNode | React.ReactNode[]
  noBorders?: boolean
  noMargin?: boolean
  toggleExpand?: () => void
  children:
    | undefined
    | React.ReactNode
    | React.ReactNode[]
    | ((a: { isExpanded?: boolean }) => React.ReactNode | React.ReactNode[])
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>

export const ExtranetTableRow = ({
  children,
  isExpanded,
  expand,
  noBorders,
  noMargin,
  toggleExpand,
  onClick,
  ...props
}: ExtranetTableRowProps) => {
  const [innerIsExpanded, setInnerIsExpanded] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!!expand === false && typeof onClick !== 'function') return null

    if (typeof onClick === 'function') {
      return onClick(event)
    }

    setInnerIsExpanded(!innerIsExpanded)
  }

  const isClickable = !!expand || typeof onClick === 'function'

  return (
    <ExtranetTableRowRoot
      className="entable__row"
      noBorders={noBorders}
      noMargin={noMargin}
      {...props}
    >
      <ExtranetTableRowWrapper
        className={joinClasses('entable__row entable__row--inner', props.className)}
        isClickable={isClickable}
        onClick={handleClick}
      >
        {typeof children === 'function'
          ? children({ isExpanded: innerIsExpanded || isExpanded })
          : children}
      </ExtranetTableRowWrapper>
      {!!expand && typeof toggleExpand === 'function' && (
        <ExtranetTableExpansion isExpanded={isExpanded || innerIsExpanded}>
          {(isExpanded || innerIsExpanded) && expand}
        </ExtranetTableExpansion>
      )}
    </ExtranetTableRowRoot>
  )
}
